import React, { useState } from 'react';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';

const ForgotPasswordView = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1); // Step 1: Enter email, Step 2: Enter code + new password
  const navigate = useNavigate();

  const handleRequestReset = async () => {
    if (!email.trim()) {
      setMessage("Please enter your email.");
      return;
    }

    try {
      setIsLoading(true);
      setMessage("");
      
      await resetPassword({ username: email });
      
      setMessage("✓ Verification code sent! Check your email.");
      setStep(2); // Move to step 2
    } catch (error) {
      console.error("Reset password error:", error);
      
      // Don't reveal if email exists or not for security
      if (error.name === 'UserNotFoundException' || error.name === 'LimitExceededException') {
        setMessage("If this email exists, a verification code has been sent.");
        setStep(2); // Still move to step 2 to not reveal if email exists
      } else {
        setMessage(error.message || "An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async () => {
    if (!code.trim()) {
      setMessage("Please enter the verification code.");
      return;
    }
    if (!newPassword.trim()) {
      setMessage("Please enter a new password.");
      return;
    }
    if (newPassword.length < 8) {
      setMessage("Password must be at least 8 characters long.");
      return;
    }

    try {
      setIsLoading(true);
      setMessage("");
      
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword
      });
      
      setMessage("✓ Password reset successful! Redirecting to login...");
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error("Confirm reset error:", error);
      
      if (error.name === 'CodeMismatchException') {
        setMessage("Invalid verification code. Please try again.");
      } else if (error.name === 'ExpiredCodeException') {
        setMessage("Verification code expired. Please request a new one.");
        setStep(1); // Go back to step 1
      } else if (error.name === 'InvalidPasswordException') {
        setMessage("Password does not meet requirements. Must be at least 8 characters.");
      } else {
        setMessage(error.message || "An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setIsLoading(true);
      setMessage("");
      
      await resetPassword({ username: email });
      
      setMessage("✓ New verification code sent!");
    } catch (error) {
      console.error("Resend code error:", error);
      setMessage("Failed to resend code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="forgot-password-card" id="forgotPasswordCard">
      <h2 className="forgot-password-title" id="forgotPasswordTitle">
        {step === 1 ? "Reset Password" : "Enter Verification Code"}
      </h2>
      
      {message && (
        <div className={`forgot-password-message ${message.startsWith('✓') ? 'success' : 'error'}`} id="forgotPasswordMessage">
          {message}
        </div>
      )}
      
      <div className="forgot-password-form" id="forgotPasswordForm">
        {step === 1 ? (
          <>
            <div className="forgot-password-form-group" id="forgotPasswordEmailGroup">
              <label className="forgot-password-label" id="forgotPasswordEmailLabel">
                Email Address
              </label>
              <input 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                className="forgot-password-input"
                id="forgotPasswordEmailInput"
                placeholder="Enter your email"
                disabled={isLoading}
              />
            </div>
            
            <p className="forgot-password-description" id="forgotPasswordDescription">
              Enter your email address and we'll send you a verification code to reset your password.
            </p>
            
            <button 
              onClick={handleRequestReset}
              className={`forgot-password-btn ${isLoading ? 'forgot-password-btn-loading' : ''}`}
              id="forgotPasswordRequestBtn"
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : "Send Verification Code"}
            </button>
          </>
        ) : (
          <>
            <div className="forgot-password-form-group" id="forgotPasswordCodeGroup">
              <label className="forgot-password-label" id="forgotPasswordCodeLabel">
                Verification Code
              </label>
              <input 
                type="text" 
                value={code} 
                onChange={(e) => setCode(e.target.value)} 
                className="forgot-password-input"
                id="forgotPasswordCodeInput"
                placeholder="Enter 6-digit code"
                disabled={isLoading}
              />
            </div>
            
            <div className="forgot-password-form-group" id="forgotPasswordNewPasswordGroup">
              <label className="forgot-password-label" id="forgotPasswordNewPasswordLabel">
                New Password
              </label>
              <input 
                type="password" 
                value={newPassword} 
                onChange={(e) => setNewPassword(e.target.value)} 
                className="forgot-password-input"
                id="forgotPasswordNewPasswordInput"
                placeholder="Enter new password (min. 8 characters)"
                disabled={isLoading}
              />
            </div>
            
            <button 
              onClick={handleConfirmReset}
              className={`forgot-password-btn ${isLoading ? 'forgot-password-btn-loading' : ''}`}
              id="forgotPasswordConfirmBtn"
              disabled={isLoading}
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </button>
            
            <button 
              onClick={handleResendCode}
              className="forgot-password-resend-btn"
              id="forgotPasswordResendBtn"
              disabled={isLoading}
            >
              Resend Code
            </button>
          </>
        )}
        
        <button 
          onClick={() => navigate('/login')}
          className="forgot-password-back-btn"
          id="forgotPasswordBackBtn"
        >
          Back to Login
        </button>
      </div>
    </div>
  );
};

export default ForgotPasswordView;
